import React from 'react';
import PropType from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import Modal from '../Modal/Modal';
import ModalBody from '../Modal/ModalBody';
import Button from '../Button/Button';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import themeColors from '../../assets/styles/themeColors';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { variables } from '../../assets/styles/variables';
import { ReactComponent as CheckBlank } from '../../assets/images/svg/check-blank.svg';

const ButtonContainer = styled.div`
  display: flex;
  align-self: center;

  padding: ${pxToRem(16)};
`;

const ModalBodyContainer = styled.div`
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};
`;

const ModalSubtitle = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  padding-top: ${pxToRem(16)};
  padding-left: ${pxToRem(16)};
  ${(props) => props.css}

  ${mediaBelow(variables.breakpoints.bpMd)} {
    padding: ${pxToRem(16)};
  }
`;

const VoidedCheckText = styled.div`
  display: flex;
  font-weight: bold;
  font-size: ${pxToRemMd(16)};
  margin-left: ${pxToRemMd(4)};
  text-decoration: underline;
`;

const VoidedCheck = styled(CheckBlank)`
  position: relative;
  height: ${pxToRem(256)};
  padding-top: ${pxToRem(18)};
  padding-left: ${pxToRem(18)};
  color: red;
`;

const VoidOverlay = styled.div`
  top: 30%;
  left: 15%;
  opacity: 30%;
  position: absolute;
  font-weight: bold;
  font-size: ${pxToRemMd(112)};
`;

const VerifyAccountModal = ({
  className,
  isOpen,
  close,
  overModal,
  avoidClearingStorage,
  handleOutsideClick,
  bankAccount,
}) => {
  const { t } = useTranslation();
  const { UploadDocumentUrlFormat } = useSelector(selectSettings);

  return (
    <Modal
      isOpen={isOpen}
      className={className}
      close={close}
      title={t(`verifyModal.title`)}
      size="lg"
      overModal={overModal}
      avoidClearingStorage={avoidClearingStorage}
      handleOutsideClick={handleOutsideClick}
    >
      <ModalBodyContainer>
        <ModalSubtitle>
          {t('verifyModal.subtitle')}
          <VoidedCheckText>{t('verifyModal.voidedCheck')}</VoidedCheckText>
        </ModalSubtitle>
        <VoidedCheck />
        <VoidOverlay>{t('verifyModal.voidOverlay')}</VoidOverlay>
        <ModalBody>{t('verifyModal.verifyText')}</ModalBody>
      </ModalBodyContainer>
      <ButtonContainer>
        <Button
          variant="primary"
          size="xl"
          onClick={() => {
            if (
              bankAccount?.EncriptedBrokerAccountUid &&
              bankAccount?.EncriptedBankAccountUid
            ) {
              const url = `${UploadDocumentUrlFormat}${encodeURIComponent(
                bankAccount?.EncriptedBrokerAccountUid,
              )}&isBankAccountDocument=true&bankAccountUid=${encodeURIComponent(
                bankAccount?.EncriptedBankAccountUid,
              )}`;
              window.open(url, '_blank').focus();
            }
          }}
        >
          {t('verifyModal.buttonText')}
        </Button>
      </ButtonContainer>
    </Modal>
  );
};

VerifyAccountModal.propTypes = {
  isOpen: PropType.bool,
  close: PropType.func,
  className: PropType.string,
  overModal: PropType.bool,
  avoidClearingStorage: PropType.bool,
  handleOutsideClick: PropType.bool,
  bankAccount: PropType.shape({
    BankAccountUid: PropType.string,
    StateType: PropType.number,
    PurposeType: PropType.number,
    BankAccountType: PropType.number,
    BankName: PropType.string,
    AccountMask: PropType.string,
    IsVerified: PropType.bool,
    VoidedCheckStatusType: PropType.number,
    MicroDepositStatusType: PropType.number,
    EncriptedBrokerAccountUid: PropType.string,
    EncriptedBankAccountUid: PropType.string,
    SetupType: PropType.number,
  }),
};

export default VerifyAccountModal;
